import type { Edge, Node } from '../../../core/models/scene';
import { isPositionNodeType, resolveOrgNodeType } from '../fields/entityFieldPacks';
import type { SuccessionLensVisibility } from '../models/successionLens';

export type SuccessorReadiness =
  | 'ready_now'
  | 'ready_1_2_years'
  | 'ready_3_plus'
  | 'emergency';

export const SUCCESSOR_READINESS_COLORS: Record<SuccessorReadiness | 'missing', string> = {
  ready_now: '#16a34a',
  ready_1_2_years: '#f59e0b',
  ready_3_plus: '#f97316',
  emergency: '#dc2626',
  missing: '#64748b',
};

export const DERIVED_SUCCESSOR_EDGE_PREFIX = 'succession-lens-derived:';

/** Canvas caption naming a succession connector for what it is. */
export const SUCCESSION_CONNECTOR_LABEL = 'Successor';

/**
 * The node whose source-authored `readyNowSuccessor` prop a derived edge was
 * minted from. Derived ids encode `<prefix><candidate>:<holder>` (URI-encoded,
 * so `:` inside node ids cannot split), and a role-card retarget appends
 * `:position:<seat>` while leaving the holder segment in place — the holder is
 * always segment two. Returns null for non-derived edge ids.
 */
export function derivedSuccessorHintHolderNodeId(edgeId: string): string | null {
  if (!edgeId.startsWith(DERIVED_SUCCESSOR_EDGE_PREFIX)) return null;
  const holder = edgeId.slice(DERIVED_SUCCESSOR_EDGE_PREFIX.length).split(':')[1];
  return holder ? decodeURIComponent(holder) : null;
}

/**
 * Presentation shared by every succession connector, whatever produced it —
 * the derived `readyNowSuccessor` fallback or a server-backed nomination.
 * Mirrors `SuccessorForConnectorManifest.defaultProps`, which only applies when
 * an edge is authored through the connector tool: the renderer reads dash and
 * arrows off `edge.attrs`, so a projected edge that omits them draws as a plain
 * solid line instead of the dashed succession lane.
 */
export function successionConnectorStyleAttrs(): Record<string, unknown> {
  return {
    dashed: true,
    dash: [8, 4],
    startArrow: { type: 'none', size: 6 },
    endArrow: { type: 'triangle', size: 8 },
    // A dashed line into a role reads as "reports to, sort of" unless it says
    // what it is. Now that nominations only surface on selection, the reader
    // meets one at a time and the word is what makes it legible.
    label: SUCCESSION_CONNECTOR_LABEL,
  };
}

export type SuccessionConnectorSide = 'left' | 'right';

export interface SuccessionConnectorPorts {
  fromAnchor: SuccessionConnectorSide;
  toAnchor: SuccessionConnectorSide;
  fromOffset: number;
  toOffset: number;
}

/**
 * Keeps succession overlays visually distinct from top/bottom reporting lines.
 * Both endpoints use the right side so the relationship stays on a consistent
 * outside lane rather than cutting back through the org-chart hierarchy.
 */
export function resolveSuccessionConnectorPorts(
  _candidate: Node,
  _target: Node,
): SuccessionConnectorPorts {
  return {
    fromAnchor: 'right',
    toAnchor: 'right',
    fromOffset: 0.5,
    toOffset: 0.5,
  };
}

export function resolveSuccessionRightSideRoute(
  from: { x: number; y: number },
  to: { x: number; y: number },
  clearance = 12,
): number[] {
  const laneX = Math.max(from.x, to.x) + clearance;
  if (Math.abs(from.y - to.y) <= 0.001) {
    return [from.x, from.y, laneX, from.y, to.x, to.y];
  }
  return [from.x, from.y, laneX, from.y, laneX, to.y, to.x, to.y];
}

const normalizeReference = (value: unknown): string | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const normalized = String(value).trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
};

const readNodeValue = (node: Node, field: string): unknown => {
  const direct = node as Node & Record<string, unknown>;
  return node.attrs?.[field] ?? node.props?.[field] ?? direct[field];
};

const nodeReferences = (node: Node): string[] => {
  const fields = [
    node.id,
    readNodeValue(node, 'personId'),
    readNodeValue(node, 'employeeNumber'),
    readNodeValue(node, 'email'),
    readNodeValue(node, 'name'),
    readNodeValue(node, 'fullName'),
    readNodeValue(node, 'preferredName'),
    node.name,
    node.text,
  ];

  return [...new Set(fields.map(normalizeReference).filter((value): value is string => value != null))];
};

const normalizeReadiness = (value: unknown): SuccessorReadiness => {
  const normalized = normalizeReference(value)?.replace(/[\s-]+/g, '_');
  switch (normalized) {
    case 'ready_1_2':
    case 'ready_1_2_year':
    case 'ready_1_2_years':
      return 'ready_1_2_years';
    case 'ready_3_5':
    case 'ready_3_plus':
    case 'ready_3_plus_years':
      return 'ready_3_plus';
    case 'emergency':
      return 'emergency';
    case 'ready_now':
    default:
      return 'ready_now';
  }
};

function isOccupiesEdge(edge: Edge): boolean {
  const relation = String((edge.attrs as Record<string, unknown> | undefined)?.relation ?? edge.type ?? '');
  return String(edge.type) === 'occupies' || relation === 'occupies';
}

interface DeriveSuccessionLensEdgesOptions {
  diagramType: string;
  nodes: readonly Node[];
  edges: readonly Edge[];
  lensActive: boolean;
}

/**
 * Projects the source-backed `readyNowSuccessor` field into a view-only edge.
 * Explicit `successor_for` relationships remain authoritative; this fallback
 * exists for CSV/sample charts that carry succession fields but no connectors.
 */
export function deriveSuccessionLensEdges({
  diagramType,
  nodes,
  edges,
  lensActive,
}: DeriveSuccessionLensEdgesOptions): Edge[] {
  if (diagramType !== 'org' || !lensActive) return [];

  const nodeByReference = new Map<string, Node | null>();
  for (const node of nodes) {
    for (const reference of nodeReferences(node)) {
      if (!nodeByReference.has(reference)) {
        nodeByReference.set(reference, node);
        continue;
      }
      const existing = nodeByReference.get(reference);
      if (existing?.id !== node.id) nodeByReference.set(reference, null);
    }
  }

  const explicitPairs = new Set(
    edges
      .filter((edge) => isOrgSuccessorEdge(diagramType, edge))
      .map((edge) => `${edge.from}\u0000${edge.to}`),
  );
  const occupiedPersonIds = new Set(
    edges.filter(isOccupiesEdge).map((edge) => edge.from),
  );
  const derived: Edge[] = [];

  for (const target of nodes) {
    // A source file may keep a former incumbent's person-level
    // `readyNowSuccessor` hint after an enacted handover. Succession belongs
    // to the Position, so once that person no longer occupies one the hint is
    // historical source evidence, not a current plan to project or count.
    if (!isPositionNodeType(resolveOrgNodeType(target)) && !occupiedPersonIds.has(target.id)) continue;
    const successorReference = normalizeReference(readNodeValue(target, 'readyNowSuccessor'));
    if (!successorReference) continue;
    const candidate = nodeByReference.get(successorReference);
    if (!candidate || candidate.id === target.id) continue;

    const pair = `${candidate.id}\u0000${target.id}`;
    if (explicitPairs.has(pair)) continue;

    explicitPairs.add(pair);
    derived.push({
      id: `${DERIVED_SUCCESSOR_EDGE_PREFIX}${encodeURIComponent(candidate.id)}:${encodeURIComponent(target.id)}`,
      type: 'successor_for' as Edge['type'],
      from: candidate.id,
      to: target.id,
      attrs: {
        relation: 'successor_for',
        readiness: normalizeReadiness(readNodeValue(candidate, 'readiness')),
        derivedFrom: 'readyNowSuccessor',
        ...successionConnectorStyleAttrs(),
      },
    });
  }

  return derived;
}

export function isOrgSuccessorEdge(diagramType: string, edge: Edge): boolean {
  if (diagramType !== 'org') return false;
  const relation = String((edge.attrs as Record<string, unknown> | undefined)?.relation ?? '');
  return String(edge.type) === 'successor_for' || relation === 'successor_for';
}

export function resolveSuccessorReadinessColor(edge: Edge): string {
  const readiness = (edge.attrs as Record<string, unknown> | undefined)?.readiness;
  return typeof readiness === 'string' && readiness in SUCCESSOR_READINESS_COLORS
    ? SUCCESSOR_READINESS_COLORS[readiness as SuccessorReadiness]
    : SUCCESSOR_READINESS_COLORS.missing;
}

interface ResolveSuccessionLensEdgeOptions {
  diagramType: string;
  edge: Edge;
  selectedIds: ReadonlySet<string>;
  visibility?: SuccessionLensVisibility;
}

export interface SuccessionLensEdgeState {
  isSuccessorEdge: boolean;
  visible: boolean;
  color?: string;
}

export function resolveSuccessionLensEdge({
  diagramType,
  edge,
  selectedIds,
  visibility,
}: ResolveSuccessionLensEdgeOptions): SuccessionLensEdgeState {
  const isSuccessorEdge = isOrgSuccessorEdge(diagramType, edge);
  if (!isSuccessorEdge) return { isSuccessorEdge, visible: true };

  // Succession connectors are a lens overlay. Draw/manual/default chart views
  // must remain structural even while a person or role is selected.
  if (visibility == null) return { isSuccessorEdge, visible: false };

  const visible = visibility === 'all'
    || selectedIds.has(edge.id)
    || selectedIds.has(edge.from)
    || selectedIds.has(edge.to);

  return {
    isSuccessorEdge,
    visible,
    // Readiness colour is a lens contribution; outside the lens the edge does
    // not render at all.
    color: resolveSuccessorReadinessColor(edge),
  };
}
